import React, { useEffect, useState } from "react";

import {
  faExclamationCircle,
  faLock,
  faPen,
  faPlus,
  faRobot,
  faRotateRight,
  faSparkles,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { times } from "lodash-es";
import Markdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { DeleteAgentModal } from "src/agents/modals/DeleteAgentModal";
import { Agent } from "src/agents/types";
import { UpsertAgentModal } from "src/agents/UpsertAgentModal";
import {
  useAgents,
  useDeleteAgent,
  usePrefetchAgentModels,
  useUpdateAgentPrebuiltConfiguration,
} from "src/api/agents/management";
import { DocumentTitle } from "src/base-components/DocumentTitle";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import {
  ResourceCard,
  ResourceCardSkeleton,
  ResourceCardsContainer,
} from "src/base-components/ResourceCard";
import {
  ResourceStatusPill,
  ResourceStatusPillContainer,
} from "src/base-components/ResourceStatusPill";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { SubHeader } from "src/flow/SubHeader";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useIsAgentsBuilderFeatureEnabled } from "src/hooks/useIsAgentsBuilderFeatureEnabled";
import { WhitePane } from "src/jobs/common/WhitePane";
import { PageHeader } from "src/layout/PageHeader";
import { getUrlToAgentVersionsPage } from "src/router/urls";
import { logger } from "src/utils/logger";
import { getApiErrorDetail, pluralize } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const AgentsPage: React.FC = () => {
  const { isEnabled: isAgentsBuilderFeatureEnabled } =
    useIsAgentsBuilderFeatureEnabled();
  const {
    isOpen: isUpsertModalOpen,
    data: agentToEdit,
    openModal: openUpsertModal,
    afterLeave,
  } = useModal<Agent | undefined>();

  // Prefetch available models for agent configuration
  const prefetchModels = usePrefetchAgentModels();
  useEffect(() => {
    prefetchModels();
  }, [prefetchModels]);

  if (!isAgentsBuilderFeatureEnabled) {
    return (
      <div className="flex h-full flex-col" data-loc="agents-page">
        <DocumentTitle segments={["Agents"]} />
        <PageHeader title="Agents" />
        <div className="decideScrollbar h-full min-w-[300px] overflow-hidden p-5">
          <WhitePane>
            <EmptyState
              dataLoc="agents-module-not-enabled"
              description="Agents aren't enabled for this workspace. This feature is required to build and manage automated decisioning agents. Ask your Solution Engineer to enable access so you can build and manage decisioning agents."
              headline="Agents not available"
              icon={faLock}
            />
          </WhitePane>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-loc="agents-page">
      <DocumentTitle segments={["Agents"]} />
      <PageHeader title="Agents" />
      <SubHeader title={null}>
        <SubHeader.Button
          dataLoc="create-agent-button"
          icon={faPlus}
          tooltip="Create Agent"
          onClick={() => openUpsertModal()}
        />
      </SubHeader>

      <div className="decideScrollbar h-full min-w-[300px] overflow-hidden p-5">
        <AgentsList
          onCreateAgent={() => openUpsertModal()}
          onEditAgent={(agent) => openUpsertModal(agent)}
        />
      </div>

      <UpsertAgentModal
        agent={agentToEdit}
        open={isUpsertModalOpen}
        onClose={afterLeave}
      />
    </div>
  );
};

const AgentsList: React.FC<{
  onCreateAgent: () => void;
  onEditAgent: (agent: Agent) => void;
}> = ({ onCreateAgent, onEditAgent }) => {
  const { data: agents, isError, isLoading, refetch, isFetching } = useAgents();
  const { mutateAsync: deleteAgent } = useDeleteAgent();
  const { mutateAsync: updatePrebuiltConfiguration } =
    useUpdateAgentPrebuiltConfiguration();
  const navigate = useNavigate();
  const { orgId, wsId } = useParamsDecode<{ orgId: string; wsId: string }>();
  const { prebuiltAgents } = useCapabilities();
  const canManagePrebuilt = prebuiltAgents.canWrite;
  const {
    isOpen: isDeleteModalOpen,
    data: agentToDelete,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal<Agent>();

  const [filter, setFilter] = useState<"all" | "pre-built">("all");

  const hasAnyPrebuilt = agents?.some(
    (agent) => agent.prebuilt_configuration != null,
  );

  const filteredAgents =
    filter === "pre-built"
      ? agents?.filter((agent) => agent.prebuilt_configuration != null)
      : agents;

  const handleTogglePrebuilt = async (agent: Agent) => {
    const isCurrentlyPrebuilt = agent.prebuilt_configuration != null;
    try {
      await updatePrebuiltConfiguration({
        agentId: agent.id,
        prebuiltConfiguration: isCurrentlyPrebuilt ? null : {},
      });
      toastActions.success({
        title: isCurrentlyPrebuilt
          ? "Agent is no longer a Managed Agent"
          : "Agent switched to Managed Agent",
      });
    } catch (error) {
      logger.error("Failed to convert agent", error);
      toastActions.failure({
        title: "Failed to update Agent",
        description: isAxiosError(error) ? getApiErrorDetail(error) : undefined,
      });
    }
  };

  const handleDeleteAgent = async () => {
    if (!agentToDelete) return;

    try {
      await deleteAgent(agentToDelete.id);
      toastActions.success({
        title: "Agent deleted successfully",
      });
      closeDeleteModal();
    } catch (error) {
      logger.error("Failed to delete agent", error);

      if (isAxiosError(error)) {
        if (error.response?.status === 409) {
          toastActions.failure({
            title: "Failed to delete Agent",
            description:
              "The Agent has published versions and can't be deleted",
          });
          return;
        }

        toastActions.failure({
          title: "Failed to delete Agent",
          description: getApiErrorDetail(error),
        });
        return;
      }

      toastActions.failure({
        title: "Failed to delete Agent",
        description: "An unknown error occurred",
      });
    }
  };

  if (!isLoading && isError) {
    return (
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            label: "Retry",
            onClick: () => refetch(),
            isDisabled: isFetching,
          }}
          dataLoc="agents-error-state"
          description="We're having trouble loading Agents. Please try again later or contact Taktile support if the problem persists."
          headline="Error loading Agents"
          icon={faExclamationCircle}
          variant="error"
        />
      </WhitePane>
    );
  }

  if (!isLoading && (!agents || agents.length === 0)) {
    return (
      <WhitePane>
        <EmptyState
          actionButton={{
            label: "Create agent",
            icon: faPlus,
            onClick: onCreateAgent,
          }}
          dataLoc="agents-empty-state"
          description="There are no Agents found in this workspace yet."
          headline="No Agents found"
          icon={faRobot}
        />
      </WhitePane>
    );
  }

  return (
    <>
      {hasAnyPrebuilt && (
        <div className="mb-6 flex gap-x-1" data-loc="agents-filter-tabs">
          {(["all", "pre-built"] as const).map((key) => (
            <button
              key={key}
              className={twJoin(
                "rounded-full px-4 py-1.5 font-inter-medium-13px transition-colors",
                filter === key
                  ? "bg-indigo-500 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50",
              )}
              data-loc={`agents-filter-${key}`}
              onClick={() => setFilter(key)}
            >
              {key === "all" ? "All" : "Taktile Agents"}
            </button>
          ))}
        </div>
      )}
      <ResourceCardsContainer>
        {isLoading
          ? times(3, (i) => <ResourceCardSkeleton key={i} />)
          : filteredAgents?.map((agent) => {
              const isPrebuilt = agent.prebuilt_configuration != null;
              const isReadOnly = isPrebuilt && !canManagePrebuilt;
              const options: OptionsDropdownElement[] = [
                ...(isReadOnly
                  ? []
                  : [
                      {
                        key: "Edit name & description",
                        icon: faPen,
                        action: () => onEditAgent(agent),
                      },
                    ]),
                ...(isReadOnly
                  ? []
                  : [
                      {
                        key: "Delete",
                        icon: faTrashCan,
                        action: () => openDeleteModal(agent),
                      },
                    ]),
                ...(canManagePrebuilt
                  ? [
                      {
                        key: isPrebuilt
                          ? "Switch to Customer-configurable"
                          : "Switch to Managed Agent",
                        icon: faSparkles,
                        action: () => handleTogglePrebuilt(agent),
                      },
                    ]
                  : []),
              ];

              return (
                <ResourceCard
                  key={agent.id}
                  dataLoc={`agent-card-${agent.id}`}
                  description={<Markdown>{agent.description}</Markdown>}
                  title={
                    <>
                      <div className="line-clamp-2">{agent.name}</div>
                      {isPrebuilt && (
                        <div className="mt-2">
                          <Pill size="sm" variant="indigo-light">
                            <Pill.Icon icon={faSparkles} />
                            <Pill.Text>Taktile Agents</Pill.Text>
                          </Pill>
                        </div>
                      )}
                    </>
                  }
                  onClick={() =>
                    navigate(getUrlToAgentVersionsPage(orgId, wsId, agent.id))
                  }
                >
                  <div>
                    <div className="flex items-end justify-between">
                      <ResourceStatusPillContainer>
                        {agent.published_versions_count! > 0 && (
                          <ResourceStatusPill
                            key="published"
                            color="green"
                            tooltip={{
                              title: `${pluralize(
                                agent.published_versions_count!,
                                "published version",
                              )}`,
                            }}
                            value={agent.published_versions_count!}
                          />
                        )}
                        {agent.draft_versions_count! > 0 && (
                          <ResourceStatusPill
                            key="draft"
                            color="yellow"
                            tooltip={{
                              title: `${pluralize(
                                agent.draft_versions_count!,
                                "draft version",
                              )}`,
                            }}
                            value={agent.draft_versions_count!}
                          />
                        )}
                      </ResourceStatusPillContainer>
                      {options.length > 0 && (
                        <EllipsisOptionsDropdown
                          buttonClassName="opacity-0 group-hover/resource-card:opacity-100 w-6 h-6"
                          dataLoc={`agent-options-${agent.id}`}
                          elements={options}
                        />
                      )}
                    </div>
                  </div>
                </ResourceCard>
              );
            })}
      </ResourceCardsContainer>

      <DeleteAgentModal
        agent={agentToDelete}
        isOpen={isDeleteModalOpen}
        workspaceId={wsId}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteAgent}
      />
    </>
  );
};
